/**
 * The one place a descriptor's icon name becomes a drawing.
 *
 * The backend names a *meaning* — "alarm", "radio", "maintenance" — never a
 * glyph, for the same reason it names a tone rather than a colour: what the
 * portal draws for "alarm" is a front-end decision that changes with the icon
 * set, and a contract that spelled out `NotificationsActiveIcon` would have to
 * change with it. Every name the backend may send is answered here, and a name
 * this bundle does not know renders nothing rather than breaking the control
 * around it.
 *
 * Contains:
 * - DESCRIPTOR_ICONS — icon name → component
 * - descriptorIcon — icon name → element, sized for a filter chip
 * - OptionMark — the mark an option carries beside its label
 *
 * The names come from `ICON_NAMES` in the backend's grid spec; adding one there
 * without adding it here leaves a control unadorned, never broken.
 */

import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import FlagIcon from '@mui/icons-material/Flag';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MemoryIcon from '@mui/icons-material/Memory';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import WbIncandescentIcon from '@mui/icons-material/WbIncandescent';
import { Box } from '@mui/material';
import React from 'react';

import { TONE_TO_FAMILY } from './columnRenderers';

/** Every icon name a descriptor may send, and what this bundle draws for it. */
export const DESCRIPTOR_ICONS = Object.freeze({
  alarm: NotificationsActiveIcon,
  calendar: CalendarMonthIcon,
  camera: PhotoCameraIcon,
  customer: BusinessIcon,
  device: DeveloperBoardIcon,
  feature: FlagIcon,
  firmware: MemoryIcon,
  group: GroupWorkIcon,
  health: MonitorHeartIcon,
  inventory: Inventory2Icon,
  'load-type': WbIncandescentIcon,
  location: LocationOnIcon,
  maintenance: BuildIcon,
  radio: SettingsInputAntennaIcon,
  status: RadioButtonCheckedIcon,
  user: PersonIcon,
});

/** An option mark reads a shade smaller than the chip icon beside the label. */
const OPTION_MARK_SIZE = 14;

/** The size a filter chip's icon reads at, matching the bar's own chips. */
const CHIP_ICON_SIZE = 16;

/**
 * Render the icon a descriptor asked for, or nothing.
 *
 * @param {?string} name - an icon name from the descriptor.
 * @param {number} [size] - font size in px; the filter-chip size by default.
 * @returns {?React.ReactElement} null for an absent or unknown name.
 */
export function descriptorIcon(name, size = CHIP_ICON_SIZE) {
  const Icon = name ? DESCRIPTOR_ICONS[name] : undefined;
  return Icon ? <Icon sx={{ fontSize: size }} /> : null;
}

/** An option's tone as a palette colour; neutral when it names none. */
export function toneColor(tone) {
  return `var(--status-${TONE_TO_FAMILY[tone] ?? 'neutral'})`;
}

/**
 * The mark an option carries beside its label: the icon its grid named, drawn
 * in its tone, or a plain dot in that tone when it named no icon.
 *
 * Both hints are the descriptor's, and an option inherited from an enum column
 * brings that column's — so a value looks the same in a cell and in the control
 * that selects it. An option carrying neither renders as plain text.
 *
 * @param {{icon: (string|undefined), tone: (string|undefined)}} props
 * @returns {?React.ReactElement}
 */
export function OptionMark({ icon, tone }) {
  const drawn = descriptorIcon(icon, OPTION_MARK_SIZE);
  if (drawn) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', mr: 1, color: toneColor(tone) }}>
        {drawn}
      </Box>
    );
  }
  if (!tone) return null;
  return (
    <Box
      component="span"
      sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: toneColor(tone), mr: 1, flexShrink: 0 }}
    />
  );
}
